import { Cross1Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { IconButton, TextField } from '@radix-ui/themes';
import { RootProps } from '@radix-ui/themes/components/text-field';
import * as React from 'react';

import styles from './SearchBar.module.css';
import { SearchBarSlot, SlotMode } from './SearchBarSlot';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';

interface SlotProps {
    as?: SlotMode;
    side?: TextField.SlotProps['side'];
}

interface SearchBarProps
    extends Omit<RootProps, 'onSubmit' | 'slot' | 'value'> {
    value?: string;
    slot?: SlotProps;
    onClear?: () => void;
    onSubmit?: (searchTerm: string) => void;
}

const DEFAULT_SLOT: SlotProps = {
    as: 'button',
    side: 'right',
};

export const SearchBar = ({
    slot = {},
    value = '',
    onClear,
    name = 'search',
    placeholder,
    onChange,
    onSubmit,
    ...rest
}: SearchBarProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [searchText, setSearchText] = React.useState(() => value);
    const { as, side } = mergeWithDefault(slot, DEFAULT_SLOT);
    const hasSlot = as !== undefined;

    const handleInputChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(event);
            setSearchText(event.target.value);
        },
        [onChange],
    );

    const handleClear = React.useCallback(() => {
        onClear?.();
        setSearchText('');

        if (inputRef.current) inputRef.current.focus();
    }, [onClear]);

    const handleFormSubmit = React.useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            onSubmit?.(searchText);
        },
        [onSubmit, searchText],
    );

    return (
        <form onSubmit={handleFormSubmit}>
            <TextField.Root
                className={styles.searchBar}
                ref={inputRef}
                name={name}
                placeholder={placeholder}
                value={searchText}
                onChange={handleInputChange}
                {...rest}
            >
                {searchText && (
                    <TextField.Slot side='right'>
                        <IconButton
                            className={styles.clear}
                            size={'1'}
                            variant='ghost'
                            onClick={handleClear}
                            aria-label='Clear search'
                            asChild
                        >
                            <Cross1Icon />
                        </IconButton>
                    </TextField.Slot>
                )}

                {hasSlot && (
                    <TextField.Slot side={side}>
                        <SearchBarSlot as={as} onClick={handleFormSubmit}>
                            <MagnifyingGlassIcon className={styles.icon} />
                        </SearchBarSlot>
                    </TextField.Slot>
                )}
            </TextField.Root>
        </form>
    );
};
